import React, { useEffect, useState } from 'react';
import { Label, Form, Input, InputGroup, InputGroupText, Button } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import lmsLogo from '../assets/images/lmsLogo.png';
import { sendOtp, verifyOtp } from '../helper/otp_helper';
import { registerStudent } from '../helper/student_helper';
import Loader from '../component/Loader/Loader';
import signUpBg from '../assets/images/SignUPBgCrop.PNG';
import { Eye, EyeOff } from 'lucide-react';

const SignUp = () => {
  const [loader, setLoader] = useState(false);
  const [otpField, setOtpField] = useState(false);
  const [otpVerify, setOtpVerify] = useState(false);
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [cred, setCred] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});
  const [pwChecks, setPwChecks] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    symbol: false
  });

  const navigate = useNavigate();

  // Initialize autocomplete prevention
  useEffect(() => {
    // Add a hidden input to trick browsers
    const preventAutofill = () => {
      const fakeInput = document.createElement('input');
      fakeInput.type = 'password';
      fakeInput.style.position = 'absolute';
      fakeInput.style.left = '-9999px';
      fakeInput.name = 'fake-password';
      fakeInput.autocomplete = 'new-password';
      document.body.appendChild(fakeInput);
      
      // Clean up on unmount
      return () => {
        document.body.removeChild(fakeInput);
      };
    };

    preventAutofill();
  }, []);

  // live password validation and confirm check
  useEffect(() => {
    const pw = cred.password || '';
    setPwChecks({
      length: pw.length >= 8,
      upper: /[A-Z]/.test(pw),
      lower: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      symbol: /[!@#$%^&*()_\-+=\[\]{};:'"\\|,.<>\/?`~]/.test(pw)
    });

    if (cred.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: cred.confirmPassword === cred.password ? undefined : 'Passwords do not match' }));
    } else {
      setErrors(prev => ({ ...prev, confirmPassword: undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cred.password, cred.confirmPassword]);

  const validateBasic = () => {
    const e = {};
    if (!cred.firstName.trim()) e.firstName = "First name is required";
    if (!cred.lastName.trim()) e.lastName = "Last name is required";
    if (!/^[0-9]{10}$/.test(cred.mobile)) e.mobile = "Mobile must be 10 digits";
    if (!/^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(cred.email)) e.email = "Invalid email";
    if (!cred.password) e.password = "Password is required";
    if (cred.password !== cred.confirmPassword) e.confirmPassword = "Passwords do not match";
    const pwOk = Object.values(pwChecks).every(Boolean);
    if (!pwOk) e.passwordRules = "Password must meet the rules";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleOtpSend = async () => {
    try {
      if (!validateBasic()) {
        toast.error("Please fix the errors above");
        return;
      }
      setLoader(true);
      const data = await sendOtp({ email: cred.email });
      if (data?.success) {
        toast.success(data?.message || "OTP sent");
        setOtpField(true);
      } else {
        toast.error(data?.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleOtpVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Enter a 6-digit OTP");
      return;
    }
    try {
      setLoader(true);
      const data = await verifyOtp({ email: cred.email, otp });
      if (data?.success) {
        toast.success(data?.message || "OTP verified");
        setOtpVerify(true);
        setOtpField(false);
      } else {
        toast.error(data?.message || "OTP invalid");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    if (!otpVerify) {
      toast.error("Please verify OTP first");
      return;
    }
    if (!validateBasic()) {
      toast.error("Please fix validation errors");
      return;
    }
    try {
      setLoader(true);
      const data = await registerStudent(cred);
      if (data?.success) {
        toast.success(data?.message || "Registered successfully");
        navigate('/login');
      } else {
        toast.error(data?.message || "Registration failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const onChange = (key, value) => {
    setCred(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const PwLine = ({ ok, text }) => (
    <div className={`pw-line ${ok ? 'ok' : 'bad'}`}>
      <span className="dot">{ok ? '✓' : '•'}</span>
      <small>{text}</small>
    </div>
  );

  const canSendOtp = () => {
    const basicOk = cred.firstName && cred.lastName && /^[0-9]{10}$/.test(cred.mobile) &&
      /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(cred.email) &&
      cred.password && cred.confirmPassword && (cred.password === cred.confirmPassword) &&
      Object.values(pwChecks).every(Boolean);
    return basicOk;
  };

  // Handle password focus
  const handlePasswordFocus = () => {
    setIsPasswordFocused(true);
  };

  const handlePasswordBlur = () => {
    // Keep showing for a moment if there's content, then hide
    setTimeout(() => {
      if (!cred.password) {
        setIsPasswordFocused(false);
      }
    }, 200);
  };

  return (
    <>
      {loader && <Loader />}

      <style>{`
/* Full-bleed page - no side gutters */
.signup-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: #f6f9fc;
  overflow-x: hidden;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  margin: 0;
  padding: 0;
}

/* wrapper is full-bleed and fills viewport */
.signup-wrap {
  width: 100%;
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  position: relative;
  background: transparent;
  min-height: 100vh;
}

/* decorative background behind everything (large screens) */
.signup-wrap .bg-decor {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: url("${signUpBg}");
  background-size: cover;
  background-position: center center; /* centered */
  opacity: 0.12;
  filter: blur(8px) saturate(0.9);
  transform: scale(1.02);
  pointer-events: none;
}

/* HERO column ~60% */
.hero-col {
  flex: 0 0 60%;
  min-width: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 48px;
  z-index: 2;
  overflow: hidden;           /* crop sides so image hides a little left & right */
}

/* make the hero image centered and cover the area */
.hero-bg {
  position: absolute;
  inset: 0;
  background-image: url("${signUpBg}");
  background-size: cover;
  background-position: center center; /* center the image */
  z-index: 1;
  opacity: 0.95;
  filter: saturate(0.95);
  transform: translateZ(0);
}

/* overlay for hero text */
.hero-overlay {
  position: relative;
  z-index: 2;
  max-width: 720px;
  background: linear-gradient(90deg, rgba(0,0,0,0.46), rgba(0,0,0,0.18));
  border-radius: 12px;
  padding: 36px;
  color: #fff;
  box-shadow: 0 8px 30px rgba(2,6,23,0.18);
  transform: translateY(8px);
  animation: fadeSlideIn 700ms ease both;
}

/* FORM column ~40% and fills its area fully (no visible gutter) */
.form-col {
  flex: 0 0 40%;
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;                 /* remove padding so no side gutter shows */
  z-index: 2;
  background: transparent;
}

/* form-card now stretches full width of the column; card has inner padding */
.form-card {
  width: 100%;
  height: 100%;
  display: block;
  max-width: none;
  border-radius: 0;
  background: #fff;
  padding: 40px;              /* inner padding for fields */
  box-shadow: none;           /* optional: keep minimal shadow */
  box-sizing: border-box;
}

/* layout piece sizes inside card kept comfortable */
.form-inner {
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
}

/* rest of UI */
.form-logo { display:flex; align-items:center; gap:10px; justify-content:center; margin-bottom: 6px; }
.form-title { text-align:center; margin-bottom:4px; font-weight:700; }
.form-sub { text-align:center; color:#6b7280; margin-bottom:14px; font-size:14px; }

.form-row { margin-bottom:12px; }
.input-field { 
  border-radius:8px; 
  height:44px; 
  padding:8px 12px; 
  background-color: #fff !important;
}

/* Password helper styles with animation */
.pw-helper { 
  background:#fbfbfe; 
  border-radius:8px; 
  padding:10px; 
  font-size:13px; 
  margin-top:8px; 
  border: 1px solid rgba(15,25,40,0.04); 
  max-height: 200px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.pw-helper.hidden {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
  border: 1px solid transparent;
  opacity: 0;
}

.pw-line { display:flex; align-items:center; gap:8px; margin:6px 0; color:#6b7280; }
.pw-line.ok { color: #0ea5a0; font-weight:600; }
.pw-line .dot { width:18px; display:inline-flex; align-items:center; justify-content:center; font-weight:700; }

.otp-row { display:flex; gap:8px; align-items:center; margin-top:8px; }
.btn-row { display:flex; gap:10px; margin-top:12px; justify-content:flex-end; }
.btn-primary { background: linear-gradient(90deg,#0b78ff,#0366cf); border:none; color:#fff; }
.form-foot { text-align:center; margin-top:12px; font-size:13px; color:#9aa4b2; }

@keyframes fadeSlideIn { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
@keyframes fadeSlideUp { from { opacity:0; transform: translateY(18px); } to { opacity:1; transform: translateY(0); } }

/* Mobile: hide hero, form fills screen (full-bleed), same as before */
@media (max-width: 991.98px) {
  .signup-wrap { min-height: 100vh; }
  .hero-col { display: none; }
  .form-col {
    flex: 1 1 100%;
    padding: 28px;
    background-image: linear-gradient(rgba(6,10,20,0.46), rgba(6,10,20,0.36)), url("${signUpBg}");
    background-size: cover;
    background-position: center center;
    min-height: 100vh;
    align-items:flex-start;
    justify-content:flex-start;
  }
  .form-card { width: 92%; margin-top: 36px; padding: 22px; border-radius: 12px; box-shadow: 0 18px 50px rgba(2,6,23,0.16); }
  .signup-wrap .bg-decor { display:none; }
}

/* Prevent autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
  box-shadow: 0 0 0 30px white inset !important;
  -webkit-text-fill-color: #333 !important;
  transition: background-color 5000s ease-in-out 0s;
}
`}</style>

      <div className="signup-page" role="main" aria-label="Sign up">
        <div className="signup-wrap">
          <div className="bg-decor" aria-hidden="true" />

          {/* HERO (left on desktop) */}
          <div className="hero-col" role="region" aria-label="Hero">
            <div className="hero-bg" aria-hidden="true" />
            <div className="hero-overlay" role="article">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 34, fontWeight: 800, marginBottom: 8 }}>Learn skills that get you hired.</div>
                  <div style={{ marginBottom: 16, color: 'rgba(255,255,255,0.92)' }}>Project-driven courses, mentorship, and placement support — built for builders.</div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Live Projects</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Mentor-Led</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Career Support</div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', padding: '8px 12px', borderRadius: 999, fontWeight: 600 }}>Certificate</div>
                  </div>

                  <div style={{ marginTop: 16, color: 'rgba(255,255,255,0.9)' }}>
                    <small>Join thousands of learners who've transformed their careers.</small>
                  </div>
                </div>

                <div style={{ width: 92, height: 92, borderRadius: 12, background: 'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))', transform: 'rotate(12deg)' }} aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* FORM (right on desktop) - fills the entire form column with no side gutter */}
          <div className="form-col" role="region" aria-label="Sign up form">
            <div className="form-card" role="form" aria-labelledby="signup-heading">
              <div className="form-inner">
                <div className="form-logo">
                  <Link to="/" aria-label="home"><img src={lmsLogo} alt="logo" height="44" /></Link>
                </div>

                <h3 id="signup-heading" className="form-title">Create your account</h3>
                <div className="form-sub">Sign up to access courses, projects and mentorship.</div>

                <Form onSubmit={(e) => { e.preventDefault(); }}>
                  <div className="form-row">
                    <Label for="firstName">First Name</Label>
                    <Input 
                      id="firstName" 
                      value={cred.firstName} 
                      onChange={e => onChange('firstName', e.target.value)} 
                      className="input-field" 
                      placeholder="John" 
                      autoComplete="off"
                      name="fname" // Random name to prevent autofill
                    />
                    {errors.firstName && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.firstName}</div>}
                  </div>

                  <div className="form-row">
                    <Label for="lastName">Last Name</Label>
                    <Input 
                      id="lastName" 
                      value={cred.lastName} 
                      onChange={e => onChange('lastName', e.target.value)} 
                      className="input-field" 
                      placeholder="Doe" 
                      autoComplete="off"
                      name="lname" // Random name to prevent autofill
                    />
                    {errors.lastName && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.lastName}</div>}
                  </div>

                  <div className="form-row">
                    <Label for="mobile">Mobile</Label>
                    <Input 
                      id="mobile" 
                      value={cred.mobile} 
                      onChange={e => onChange('mobile', e.target.value)} 
                      className="input-field" 
                      placeholder="9876543210" 
                      type="tel" 
                      autoComplete="off"
                      name="new-mobile" // Different from standard "phone"
                    />
                    {errors.mobile && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.mobile}</div>}
                  </div>

                  <div className="form-row">
                    <Label for="email">Email</Label>
                    <Input 
                      id="email" 
                      value={cred.email} 
                      onChange={e => onChange('email', e.target.value)} 
                      className="input-field" 
                      placeholder="you@company.com" 
                      type="email" 
                      autoComplete="off"
                      name="new-email" // Different from standard "email"
                    />
                    {errors.email && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.email}</div>}
                  </div>

                  <div className="form-row">
                    <Label for="password">Password</Label>
                    <InputGroup>
                      <Input 
                        id="password" 
                        value={cred.password} 
                        onChange={e => onChange('password', e.target.value)} 
                        className="input-field" 
                        type={showPassword ? 'text' : 'password'} 
                        placeholder="Create a password" 
                        autoComplete="new-password" // Standard way to prevent autofill
                        name="new-password" // Different from standard "password"
                        onFocus={handlePasswordFocus}
                        onBlur={handlePasswordBlur}
                      />
                      <InputGroupText style={{ border: 'none', background: 'transparent', padding: 0 }}>
                        <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(s => !s)} style={{ border: 'none', background: 'transparent', padding: '8px 10px', cursor:'pointer' }}>
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </InputGroupText>
                    </InputGroup>

                    {/* Password helper - only shows when focused or has content */}
                    <div className={`pw-helper ${isPasswordFocused || cred.password ? '' : 'hidden'}`} aria-live="polite">
                      <PwLine ok={pwChecks.length} text="At least 8 characters" />
                      <PwLine ok={pwChecks.upper} text="1 uppercase letter (A-Z)" />
                      <PwLine ok={pwChecks.lower} text="1 lowercase letter (a-z)" />
                      <PwLine ok={pwChecks.number} text="1 number (0-9)" />
                      <PwLine ok={pwChecks.symbol} text="1 symbol (e.g. !@#$%)" />
                    </div>
                    {errors.passwordRules && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.passwordRules}</div>}
                  </div>

                  <div className="form-row">
                    <Label for="confirmPassword">Confirm Password</Label>
                    <Input 
                      id="confirmPassword" 
                      value={cred.confirmPassword} 
                      onChange={e => onChange('confirmPassword', e.target.value)} 
                      className="input-field" 
                      type="password" 
                      placeholder="Confirm password" 
                      autoComplete="new-password"
                      name="confirm-new-password"
                    />
                    {errors.confirmPassword && <div style={{ color: '#d9534f', marginTop: 8 }}>{errors.confirmPassword}</div>}
                  </div>

                  {otpField && !otpVerify && (
                    <div className="form-row">
                      <Label for="otp">OTP</Label>
                      <div className="otp-row">
                        <Input 
                          id="otp" 
                          value={otp} 
                          onChange={e => setOtp(e.target.value)} 
                          className="input-field" 
                          placeholder="6-digit OTP" 
                          autoComplete="off"
                          name="otp-code"
                        />
                        <Button color="secondary" outline onClick={handleOtpVerify} disabled={loader}>Verify</Button>
                      </div>
                    </div>
                  )}

                  <div className="btn-row">
                    {!otpVerify && (
                      <Button color="primary" className="btn-primary" onClick={handleOtpSend} disabled={!canSendOtp() || loader}>
                        {loader ? 'Sending...' : 'Send OTP'}
                      </Button>
                    )}

                    {otpVerify && (
                      <Button color="primary" className="btn-primary" onClick={handleSubmit} disabled={loader}>
                        {loader ? 'Submitting...' : 'Create account'}
                      </Button>
                    )}
                  </div>

                  <div className="form-foot">
                    <small>Already have an account? <Link to="/login">Login</Link></small>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 10, color: '#c1c9d6', fontSize: 12 }}>
                    © {new Date().getFullYear()} SS — Created By Amass Skill Ventures
                  </div>
                </Form>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SignUp;